/**
 * API Service Layer
 *
 * Centralized HTTP client with Auth0 token management, request/response interceptors,
 * and automatic error handling for all API calls.
 */

import type { ApiPermissionsResponse } from "../types";
import { createRisClient } from '../api/risClient';
import { getApiUrl } from '../api/apiConfig';

/**
 * Enum for device filtering options
 * Values match API productFamily field values directly
 */
export enum DeviceFilter {
  ALL = '',
  CAMERAS = 'Camera',
  PANELS = 'SecurityPanel',
  THERMOSTATS = 'Thermostat',
  WATER_LEAK = 'LeakDetector'
}

/**
 * Maps DeviceFilter values to one or more API productFamily values.
 * Only entries that need to match MULTIPLE families are listed here.
 * Single-family filters fall back to using the enum value directly.
 * CAMERAS includes both 'Camera' and 'DoorBell' (video doorbells like VX1)
 * since they are all VIDEO-category devices managed by the camera pilet.
 */
const DEVICE_FILTER_FAMILIES: Record<string, string[]> = {
  [DeviceFilter.CAMERAS]: ['Camera', 'DoorBell'],
};

/**
 * TypeScript interfaces for consumer data structure (used in filtering)
 */
interface FilterConsumerDevice {
  id?: string;
  name?: string;
  consumerDeviceLocationId?: string;
  device?: ConsumerDeviceDetail;
  createdAt?: string;
  updatedAt?: string;
}

interface FilterLocation {
  id?: string;
  name?: string;
  consumerAccountId?: string;
  address?: Record<string, unknown>;
  geoCoordinate?: Record<string, unknown>;
  consumerDevices?: FilterConsumerDevice[];
  weatherLocationId?: string;
  timeZone?: string;
}

interface FilterConsumerAccount {
  id?: string;
  consumerAccountClassification?: string;
  locations?: FilterLocation[];
}


interface PendingService {
  partnerAccountId?: string | null;
  partnerAccountName?: string | null;
  type?: string | null;
  consumerDeviceLocationId?: string | null;
  partnerDeviceServices?: {
    deviceId?: string | null;
    serviceType?: string | null;
    status?: string | null;
  }[] | null;
}

interface FilterConsumerUser {
  id?: string;
  role?: string;
  consumerAccount?: FilterConsumerAccount;
  consumerUserAccessSchedules?: unknown[];
}

interface FilterConsumerData {
  consumerUsers?: FilterConsumerUser[];
  id?: string;
  firstName?: string;
  lastName?: string;
  contactEmail?: string;
  primaryPhoneNumber?: string;
  countryCode?: string;
  locale?: string;
  pendingServices?: PendingService[] | null;
  consentRequired?: boolean;
}

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Options forwarded to Auth0's getAccessTokenSilently.
 * `cacheMode: 'off'` forces a network round-trip using the refresh token,
 * bypassing the in-memory / localStorage token cache.
 */
export interface GetTokenOptions {
  cacheMode?: "on" | "cache-only" | "off";
}

// Global Auth0 token getter - set by App.tsx (options-aware)
let getAuth0Token: ((opts?: GetTokenOptions) => Promise<string>) | null = null;

// Global logout callback - set by AuthContext
let onUnauthorized: (() => void) | null = null;

/**
 * Set the Auth0 token getter function (options-aware).
 * Call this from App.tsx after Auth0Provider is mounted.
 */
export function setAuth0TokenGetter(
  getter: (opts?: GetTokenOptions) => Promise<string>
) {
  getAuth0Token = getter;
}

/**
 * Set the unauthorized callback (triggers logout)
 * Call this from AuthContext on mount
 */
export function setUnauthorizedCallback(callback: () => void) {
  onUnauthorized = callback;
}

/**
 * Get the Auth0 token getter function
 * Used by pilets to access Auth0 tokens
 */
export function getAuth0TokenGetter(): ((opts?: GetTokenOptions) => Promise<string>) | null {
  return getAuth0Token;
}

/**
 * Filters consumer account data based on device type.
 * @param consumerData - The consumer data to filter
 * @param filterType - DeviceFilter enum value (matches API productFamily)
 * @returns Filtered consumer data based on the device filter type
 */
export function filterConsumerDataByDeviceType(
  consumerData: FilterConsumerData,
  filterType: DeviceFilter = DeviceFilter.ALL
): FilterConsumerData {
  // If filter is ALL, return all data without filtering
  if (filterType === DeviceFilter.ALL || !consumerData?.consumerUsers) {
    return consumerData;
  }

  const consumerUsers = consumerData.consumerUsers ?? [];
  const filteredUsers: FilterConsumerUser[] = [];

  for (const consumerUser of consumerUsers) {
    const locations = consumerUser?.consumerAccount?.locations ?? [];
    const filteredLocations: FilterLocation[] = [];

    for (const location of locations) {
      const devices = location?.consumerDevices ?? [];
      const allowedFamilies = DEVICE_FILTER_FAMILIES[filterType] ?? [filterType];
      const filteredDevices = devices.filter(
        (device: FilterConsumerDevice) => {
          const family = device?.device?.product?.productFamily;
          return family != null && allowedFamilies.includes(family);
        }
      );

      filteredLocations.push({
        ...location,
        consumerDevices: filteredDevices,
      });
    }

    filteredUsers.push({
      ...consumerUser,
      consumerAccount: {
        ...consumerUser?.consumerAccount,
        locations: filteredLocations,
      },
    });
  }

  return {
    ...consumerData,
    consumerUsers: filteredUsers,
  };
}

export class ApiService {
  private baseURL: string;
  private tokenKey = "oneportal_auth_token";
  private refreshTokenKey = "oneportal_refresh_token";
  private useMockAuth: boolean;

  constructor(baseURL: string = "/api") {
    this.baseURL = baseURL;
    this.useMockAuth = import.meta.env.VITE_USE_MOCK_AUTH === "true";
  }

  // ============================================================================
  // Token Management
  // ============================================================================

  /**
   * Set authentication token (JWT)
   */
  setAuthToken(token: string, refreshToken?: string): void {
    sessionStorage.setItem(this.tokenKey, token);
    if (refreshToken) {
      sessionStorage.setItem(this.refreshTokenKey, refreshToken);
    }
  }

  /**
   * Force-refresh the Auth0 token by bypassing the SDK cache.
   * Called by the 401 retry interceptors in apiService and apiConfig.
   * Returns the fresh token or null on failure.
   */
  async forceRefreshToken(): Promise<string | null> {
    if (this.useMockAuth || !getAuth0Token) return null;
    try {
      const token = await getAuth0Token({ cacheMode: "off" });
      // Keep the localStorage fallback up to date
      localStorage.setItem("authToken", token);
      return token;
    } catch (error) {
      console.error("[ApiService] Force token refresh failed:", error);
      return null;
    }
  }

  /**
   * Get current authentication token
   * In Auth0 mode: gets token from Auth0 SDK
   * In mock mode: gets token from sessionStorage
   */
  async getAuthToken(): Promise<string | null> {
    // In Auth0 mode, use Auth0 token
    if (!this.useMockAuth && getAuth0Token) {
      try {
        return await getAuth0Token();
      } catch (error) {
        console.error("Failed to get Auth0 token:", error);

        // Check if it's a timeout error (R4: Timeout)
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('Timeout') || errorMessage.includes('R4:')) {
          console.error("⏱️ Auth0 token fetch timeout - session may be expired");

          // After multiple timeouts, trigger logout
          // This prevents infinite retry loops
          const timeoutKey = 'auth0_timeout_count';
          const timeoutCount = parseInt(sessionStorage.getItem(timeoutKey) || '0');

          if (timeoutCount >= 3) {
            console.error("🚪 Too many Auth0 timeouts, triggering logout");
            sessionStorage.removeItem(timeoutKey);

            if (onUnauthorized) {
              setTimeout(() => onUnauthorized!(), 500);
            }
            throw new Error("AUTH0_TIMEOUT_EXCEEDED");
          } else {
            sessionStorage.setItem(timeoutKey, String(timeoutCount + 1));
          }
        }

        return null;
      }
    }

    // In mock mode, use sessionStorage token
    return sessionStorage.getItem(this.tokenKey);
  }

  /**
   * Get refresh token (mock mode only)
   */
  getRefreshToken(): string | null {
    return sessionStorage.getItem(this.refreshTokenKey);
  }

  /**
   * Clear all authentication tokens
   */
  clearAuthToken(): void {
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.refreshTokenKey);
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const token = await this.getAuthToken();
    return !!token;
  }

  // ============================================================================
  // Request/Response Interceptors
  // ============================================================================

  /**
   * Execute a fetch request and, on a 401, force-refresh the token and retry
   * exactly once before returning the response to `handleResponse`.
   * This prevents spurious logouts caused by an expired token that the Auth0
   * SDK hasn't proactively refreshed yet.
   */
  private async fetchWithRetry(
    url: string,
    config: RequestConfig
  ): Promise<Response> {
    const request = await this.buildRequest(url, config);
    let response = await fetch(request);

    if (response.status === 401 && !this.useMockAuth) {
      const freshToken = await this.forceRefreshToken();
      if (freshToken) {
        // Inject the fresh token directly rather than calling buildRequest with
        // getAuthToken() again, which could theoretically read a stale cached
        // value if the Auth0 SDK hasn't finished updating its internal cache.
        const retryRequest = await this.buildRequest(url, config, freshToken);
        response = await fetch(retryRequest);
      }
    }

    return response;
  }

  /**
   * Synchronous token getter for the RIS SDK client.
   * Mirrors the getAccessToken parameter pattern used in eventHistoryService.ts.
   * localStorage 'authToken' is kept fresh by forceRefreshToken() on every
   * Auth0 token renewal, so this is always up to date.
   */
  private getAccessToken = (): string | null => localStorage.getItem('authToken');

  /**
   * Build request with authentication and headers
   */
  private async buildRequest(
    url: string,
    config: RequestConfig = {},
    tokenOverride?: string
  ): Promise<Request> {
    const { skipAuth, headers, ...restConfig } = config;

    const requestHeaders = new Headers(headers);

    // Add Content-Type if not set
    if (!requestHeaders.has("Content-Type")) {
      requestHeaders.set("Content-Type", "application/json");
    }

    // Add Authorization header if token exists and not skipped
    if (!skipAuth) {
      const token = tokenOverride ?? await this.getAuthToken();
      if (token) {
        requestHeaders.set("Authorization", `Bearer ${token}`);
      }
    }

    const fullURL = url.startsWith("http") ? url : `${this.baseURL}${url}`;

    return new Request(fullURL, {
      ...restConfig,
      headers: requestHeaders,
    });
  }

  /**
   * Handle response and errors.
   *
   * NOTE: By the time this method is called the `fetchWithRetry` wrapper has
   * already attempted one token force-refresh and retry on 401.  A 401 here
   * therefore means the refresh token is also expired / revoked and we must
   * log the user out.
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle 401 Unauthorized - refresh-token is expired / revoked
    if (response.status === 401) {
      console.error("🔒 401 Unauthorized: Session expired (refresh token invalid)");

      // Clear timeout counter
      sessionStorage.removeItem('auth0_timeout_count');

      // In Auth0 mode, a 401 after retry means the session is truly done
      if (!this.useMockAuth) {
        console.log("🚪 Triggering automatic logout (session expired)");

        // Store current location for return after re-login
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/login' && currentPath !== '/callback') {
          sessionStorage.setItem('oneportal_return_to', currentPath);
        }

        // Mark as session expired (not idle logout)
        sessionStorage.setItem('oneportal_session_expired', 'true');

        // Trigger logout via callback
        if (onUnauthorized) {
          // Small delay to allow user to see any error messages
          setTimeout(() => {
            onUnauthorized!();
          }, 500);
        } else {
          // Fallback: redirect to login
          console.warn("⚠️ No logout callback registered, redirecting to login");
          window.location.href = '/login';
        }

        throw new Error("SESSION_EXPIRED");
      }

      // Mock mode: attempt token refresh
      const refreshToken = this.getRefreshToken();
      if (refreshToken) {
        try {
          const newToken = await this.refreshAuthToken(refreshToken);
          if (newToken) {
            throw new Error("UNAUTHORIZED");
          }
        } catch (error) {
          this.clearAuthToken();
          throw new Error("UNAUTHORIZED");
        }
      } else {
        this.clearAuthToken();
        throw new Error("UNAUTHORIZED");
      }
    }

    // Handle 403 Forbidden - insufficient permissions
    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    // Handle 404 Not Found
    if (response.status === 404) {
      throw new Error("NOT_FOUND");
    }

    // Handle server errors (500+)
    if (response.status >= 500) {
      throw new Error("SERVER_ERROR");
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    // Parse JSON response
    const contentType = response.headers.get("Content-Type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }

    // Return text for non-JSON responses
    return response.text() as any;
  }

  /**
   * Refresh authentication token
   */
  private async refreshAuthToken(refreshToken: string): Promise<string | null> {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setAuthToken(data.accessToken, data.refreshToken);
        return data.accessToken;
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  // ============================================================================
  // HTTP Methods
  // ============================================================================

  /**
   * GET request
   */
  async get<T = any>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.fetchWithRetry(url, {
      ...config,
      method: "GET",
    });
    return this.handleResponse<T>(response);
  }

  /**
   * POST request
   */
  async post<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.fetchWithRetry(url, {
      ...config,
      method: "POST",
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * PUT request
   */
  async put<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.fetchWithRetry(url, {
      ...config,
      method: "PUT",
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * PATCH request
   */
  async patch<T = any>(
    url: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    const response = await this.fetchWithRetry(url, {
      ...config,
      method: "PATCH",
      body: JSON.stringify(data),
    });
    return this.handleResponse<T>(response);
  }

  /**
   * DELETE request
   */
  async delete<T = any>(url: string, config?: RequestConfig): Promise<T> {
    const response = await this.fetchWithRetry(url, {
      ...config,
      method: "DELETE",
    });
    return this.handleResponse<T>(response);
  }

  // ============================================================================
  // Specific API Endpoints
  // ============================================================================

  /**
   * Fetch roles and permissions from backend
   */
  async fetchPermissions(
    context: string = "CONSUMER"
  ): Promise<ApiPermissionsResponse> {
    return this.get<ApiPermissionsResponse>(`/permissions?context=${context}`);
  }

  /**
   * Get authenticated user's available contexts
   * Returns list of contexts the user has access to (CONSUMER, PARTNER, etc.)
   */
  async getUserContexts(): Promise<{
    contexts: Array<{
      context: string;
      contextDisplayName: string;
      roleName: string;
    }>;
  }> {
    // Use generated RIS Public API client
    const { risPublicApiClient } = await import("@/api/apiConfig");

    const response = await risPublicApiClient.GET("/api/v1/users/my");

    if (response.error || !response.data?.data) {
      throw new Error("Failed to fetch user profile");
    }

    const userData = response.data.data;
    const contexts: Array<{
      context: string;
      contextDisplayName: string;
      roleName: string;
    }> = [];

    // Add CONSUMER context if user has consumer accounts
    if (userData.consumerUsers && userData.consumerUsers.length > 0) {
      contexts.push({
        context: "CONSUMER",
        contextDisplayName: userData.consumerUsers[0].consumerAccountName || "My Home",
        roleName: "OWNER",
      });
    }

    // Add PARTNER context if user has partner accounts
    if (userData.partnerUsers && userData.partnerUsers.length > 0) {
      contexts.push({
        context: "PARTNER",
        contextDisplayName: userData.partnerUsers[0].partnerName || "Trade Professionals",
        roleName: userData.partnerUsers[0].role || "OWNER",
      });
    }

    return { contexts };
  }

  /**
   * Get permissions for a specific context
   * Fetches the roles and permissions for the given context (CONSUMER, PARTNER, etc.)
   */
  async getPermissionsForContext(context: string): Promise<ApiPermissionsResponse> {
    return this.fetchPermissions(context);
  }

  /**
   * Get role permissions from RIS Public API
   * Can optionally specify context (CUSTOMER/PARTNER) via query parameter
   * Endpoint: GET /api/v1/roles/rolePermissions?context={context}
   */
  async getRolePermissions(context?: string): Promise<ApiPermissionsResponse> {
    // Use generated RIS Public API client
    const { risPublicApiClient } = await import("@/api/apiConfig");

    // Build URL with optional context query parameter
    const url = context
      ? `/api/v1/roles/rolePermissions?context=${context}`
      : "/api/v1/roles/rolePermissions";

    const { data, error } = await risPublicApiClient.GET(url as any);

    if (error || !data) {
      throw new Error(error ? String(error) : "Failed to fetch role permissions");
    }

    return data as ApiPermissionsResponse;
  }

  /**
 * Get account types for the current user
 * Returns consumerAccountTypes and partnerAccountTypes
 * Endpoint: GET /api/v1/accounts/accountTypes
 */
  async getAccountTypes(): Promise<any> {
    const { risPublicApiClient } = await import("@/api/apiConfig");
    const response = await risPublicApiClient.GET(
      "/api/v1/accounts/accountTypes" as any,
    );
    if (response.error || !response.data) {
      throw new Error(
        response.error
          ? String(response.error)
          : "Failed to fetch account types",
      );
    }
    return response.data;
  }

  /**
   * Get consumer accounts from RIS Public API
   * Returns consumer account details and associated role
    * Endpoint: GET /api/v2/accounts?accountType={accountType} (fallback to v1)
   * @param accountType - Optional account type filter
   * @param deviceFilter - DeviceFilter enum to filter devices (default: CAMERAS for backward compatibility)
  */
  async getConsumerAccounts(
    accountType?: string,
    deviceFilter: DeviceFilter = DeviceFilter.CAMERAS
  ): Promise<AccountsResponse> {
    // Use generated RIS Public API client
    const { risPublicApiClient } = await import("@/api/apiConfig");
    // Try v2 first, with accountType query param if provided
    const v2Path = accountType
      ? `/api/v2/accounts?accountType=${encodeURIComponent(accountType)}`
      : "/api/v2/accounts";
    let response = await risPublicApiClient.GET(v2Path as any);

    if (response.error) {
      response = await risPublicApiClient.GET("/api/v1/accounts");
    }

    if (response.error || !response.data) {
      throw new Error(response.error ? String(response.error) : "Failed to fetch consumer accounts");
    }

    // Filter devices based on the enum value
    // Response structure: { data: { consumerUsers: [...], id, firstName, ... }, errors: [] }
    // Filter the inner data object and return in wrapped format for pilet compatibility
    if (response.data?.data && deviceFilter !== DeviceFilter.ALL) {
      const filteredUserData = filterConsumerDataByDeviceType(response.data.data, deviceFilter);
      return { data: filteredUserData } as AccountsResponse;
    }

    return response.data as AccountsResponse;
  }

  /**
   * Merge an array of per-device camera entitlement configs into a single
   * union config using OR/union/max semantics. Extracted so both the
   * network call and any in-memory re-merge (e.g. scope change from cached
   * per-device configs) share the same logic.
   */
  private mergeCameraConfigs(
    cameraConfigs: CameraEntitlementConfig[],
  ): EntitlementConfig | undefined {
    if (cameraConfigs.length === 0) {
      return undefined;
    }

    // - Boolean fields: true if ANY device enables the feature
    // - Array fields: union of all device values
    // - Number fields: maximum across devices
    const mergeBoolean = (vals: (boolean | null | undefined)[]): boolean | undefined => {
      const defined = vals.filter((v): v is boolean => v != null);
      if (defined.length === 0) return undefined;
      return defined.some(Boolean);
    };
    const mergeStringArray = (arrs: (string[] | null | undefined)[]): string[] | undefined => {
      const defined = arrs.filter((a): a is string[] => a != null);
      if (defined.length === 0) return undefined;
      return [...new Set(defined.flat())];
    };
    const mergeMax = (vals: (number | null | undefined)[]): number | undefined => {
      const defined = vals.filter((v): v is number => v != null);
      return defined.length > 0 ? Math.max(...defined) : undefined;
    };

    return {
      camera: {
        maxDeviceLimit: mergeMax(cameraConfigs.map(c => c.maxDeviceLimit)),
        liveStreamP2P: mergeMax(cameraConfigs.map(c => c.liveStreamP2P)),
        liveStreamTurn: mergeMax(cameraConfigs.map(c => c.liveStreamTurn)),
        objectDetectionEvents: mergeStringArray(cameraConfigs.map(c => c.objectDetectionEvents)),
        aiSnapshotSearch: mergeBoolean(cameraConfigs.map(c => c.aiSnapshotSearch)),
        aiClipSearch: mergeBoolean(cameraConfigs.map(c => c.aiClipSearch)),
        snapshot: mergeBoolean(cameraConfigs.map(c => c.snapshot)),
        onDemandRecording: mergeBoolean(cameraConfigs.map(c => c.onDemandRecording)),
        storage: {
          sdCardExport: mergeMax(cameraConfigs.map(c => c.storage?.sdCardExport)),
          continousRecording: mergeBoolean(cameraConfigs.map(c => c.storage?.continousRecording)),
        },
        audio: mergeStringArray(cameraConfigs.map(c => c.audio)),
        video: mergeStringArray(cameraConfigs.map(c => c.video)),
      },
    };
  }

  /**
   * Fetch subscription entitlement configs for a specific set of device IDs.
   * Endpoint: GET /api/v1/devices/entitlementConfigs?deviceIds=id1&deviceIds=id2&...
   *
   * Response contract mirrors the legacy `/api/v2/accounts/.../devices` endpoint:
   *   { data: Array<{ id, entitlementConfig: { camera: {...} } }> }
   *
   * Returns both the merged union `entitlementConfig` (for immediate consumption
   * by permissionService) AND the per-device camera config map (so callers can
   * cache per-device entries and re-merge locally when scope changes).
   */
  async getDeviceEntitlementConfigs(
    deviceIds: string[],
  ): Promise<{
    entitlementConfig?: EntitlementConfig;
    perDevice: Record<string, CameraEntitlementConfig | null>;
  }> {
    if (!deviceIds || deviceIds.length === 0) {
      return { perDevice: {} };
    }

    const { risPublicApiClient } = await import("@/api/apiConfig");
    const params = new URLSearchParams();
    for (const id of deviceIds) {
      if (id) params.append("deviceIds", id);
    }
    const url = `/api/v1/devices/entitlementConfigs?${params.toString()}`;
    const response = await risPublicApiClient.GET(url as any);

    if (response.error) {
      throw new Error(String(response.error));
    }

    // Response shape mirrors the legacy endpoint: { data: [{ id, entitlementConfig }] }
    const items: any[] = Array.isArray((response.data as any)?.data)
      ? (response.data as any).data
      : Array.isArray(response.data)
        ? (response.data as any)
        : [];

    const perDevice: Record<string, CameraEntitlementConfig | null> = {};
    const cameraConfigs: CameraEntitlementConfig[] = [];
    for (const item of items) {
      // Prefer explicit deviceId, fall back to id (matches legacy response's `id` field)
      const key: string | undefined = item?.deviceId ?? item?.id;
      if (!key) continue;
      const camera = item?.entitlementConfig?.camera;
      if (camera == null) {
        // Device present in the response with a null entitlement — record
        // an explicit-null so callers can distinguish "device not in response"
        // (pass-through) from "device present but no subscription" (deny).
        perDevice[key] = null;
        continue;
      }
      cameraConfigs.push(camera);
      perDevice[key] = camera;
    }

    return {
      entitlementConfig: this.mergeCameraConfigs(cameraConfigs),
      perDevice,
    };
  }

  /**
   * Re-merge a set of previously-cached per-device camera configs into a
   * single union `EntitlementConfig`. Used when scope changes (e.g. location
   * switch) but no new deviceIds appeared, so a network call is skipped.
   */
  mergeCachedPerDeviceConfigs(
    perDevice: Record<string, CameraEntitlementConfig | null>,
    deviceIds: string[],
  ): EntitlementConfig | undefined {
    const configs = deviceIds
      .map(id => perDevice[id])
      .filter((c): c is CameraEntitlementConfig => c != null);
    return this.mergeCameraConfigs(configs);
  }

  /**
   * Get partner accounts from RIS Public API
   * Returns partner/company details and associated role
   * Endpoint: GET /api/v1/proAccounts
   */
  async getPartnerAccounts(): Promise<any> {
    // Use generated RIS Public API client
    const { risPublicApiClient } = await import("@/api/apiConfig");

    const response = await risPublicApiClient.GET("/api/v1/proAccounts" as any);

    if (response.error || !response.data) {
      throw new Error(response.error ? String(response.error) : "Failed to fetch partner accounts");
    }

    return response.data;
  }

  private formatApiError(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === "string") return error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }

  private findAobPartnerUser(payload: unknown): { id: string; partnerAccountId: string } | null {
    const items = Array.isArray((payload as any)?.partnerUsers)
      ? (payload as any).partnerUsers
      : [];

    const partnerUser = items.find((user: any) => {
      const partnerAccountId = user?.partnerAccountId;
      return user?.id && partnerAccountId;
    });

    if (!partnerUser) {
      return null;
    }

    return {
      id: partnerUser.id,
      partnerAccountId: partnerUser.partnerAccountId,
    };
  }

  async getMyPartnerUser(): Promise<{ id: string; partnerAccountId: string }> {
    const { risPublicApiClient } = await import("@/api/apiConfig");

    const myUserResponse = await risPublicApiClient.GET("/api/v1/users/my");
    const myUserPartner = this.findAobPartnerUser(myUserResponse.data?.data);
    if (myUserPartner) {
      return myUserPartner;
    }

    if (myUserResponse.error) {
      throw new Error(this.formatApiError(myUserResponse.error));
    }

    throw new Error("No partner user found for AOB session");
  }

  async startAobSession(partnerAccountId: string, partnerUserId: string): Promise<void> {
    const { risPublicApiClient } = await import("@/api/apiConfig");

    const response = await risPublicApiClient.POST(
      "/api/v1/companies/{partnerAccountId}/employees/{partnerUserId}/aobSession/start",
      {
        params: {
          path: {
            partnerAccountId,
            partnerUserId,
          },
        },
      },
    );

    if (response.error) {
      throw new Error(this.formatApiError(response.error));
    }
  }

  /**
 * Get all consumers for a given consumer account
 * Endpoint: GET /api/v1/accounts/{consumerAccountId}/consumers
 */
  async getConsumerUsers(consumerAccountId: string): Promise<ConsumerUser[]> {
    const { risPublicApiClient } = await import('@/api/apiConfig');
    const url = `/api/v1/accounts/${encodeURIComponent(consumerAccountId)}/consumers`;
    const response = await risPublicApiClient.GET(url as any);
    if (response.error || !response.data) {
      throw new Error(response.error ? String(response.error) : 'Failed to fetch consumer users');
    }
    const payload = response.data as any;
    const arr = Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload) ? payload
        : [];
    return arr as ConsumerUser[];
  }
  /**
   * Get all consumers for a given consumer account using the v2 endpoint.
   * Supports optional server-side filtering by roles and locationIds.
   * Endpoint: GET /api/v2/accounts/{consumerAccountId}/consumers
   */
  async getConsumerUsersV2(
    consumerAccountId: string,
    filterBy?: ConsumerFilterBy,
  ): Promise<ConsumerUser[]> {
    console.log('[getConsumerUsersV2] Called with:', { consumerAccountId, filterBy });
    const risClient = createRisClient(getApiUrl('risPublicApi'), this.getAccessToken);
    let payload: any;
    try {
      payload = await risClient.myAccounts.myAccountsGetConsumerUsersByAccountV2(
        consumerAccountId,
        filterBy ? JSON.stringify([filterBy]) : undefined,
      );
    } catch (error: any) {
      console.error('[getConsumerUsersV2] ❌ API call threw:', {
        message: error?.message,
        status: error?.status,
        body: error?.body,
      });
      throw error;
    }
    const arr = Array.isArray(payload?.data) ? payload.data
      : Array.isArray(payload) ? payload
        : [];
    return arr as ConsumerUser[];
  }

  /**
   * Get a single consumer user's detail including their location assignments.
   * Endpoint: GET /api/v1/accounts/{consumerAccountId}/consumers/{consumerUserId}
   */
  async getConsumerUserDetail(
    consumerAccountId: string,
    consumerUserId: string,
  ): Promise<ConsumerUserDetailResponse> {
    const risClient = createRisClient(getApiUrl('risPublicApi'), this.getAccessToken);
    const payload = await risClient.myAccounts.myAccountsGetConsumerUser(consumerAccountId, consumerUserId) as any;
    return payload as ConsumerUserDetailResponse;
  }

  /**
   * Send employee invite (v2) — account-level, all device IDs in one request
   * POST /v2/accounts/{consumerAccountId}/invites
   */
  async sendInviteV2(consumerAccountId: string, body: InviteUserRequest): Promise<InviteUserResponse> {
    const risClient = createRisClient(getApiUrl('risPublicApi'), this.getAccessToken);
    const payload = await risClient.myAccounts.myAccountsCreateAndSendUserInviteByAccount(
      consumerAccountId,
      body as any,
    ) as any;
    return payload as InviteUserResponse;
  }

  /**
   * Update an existing invite — used to modify device access (e.g. remove a location's devices).
   * PUT /v1/accounts/{consumerAccountId}/invites
   */
  async updateInvite(
    consumerAccountId: string,
    body: import('@resideo/web-integration-services-api-client').UpdateUserInviteDataModel,
  ): Promise<void> {
    const risClient = createRisClient(getApiUrl('risPublicApi'), this.getAccessToken);
    await risClient.myAccounts.myAccountsUpdateUserInviteDataByAccount(consumerAccountId, body);
  }

  /**
   * Resend a pending invitation
   * PUT /v1/accounts/{consumerAccountId}/invites/{inviteId}/send
   */
  async resendInvite(consumerAccountId: string, inviteId: string, body: ResendInviteRequest): Promise<void> {
    await this.put<void>(`/v1/accounts/${encodeURIComponent(consumerAccountId)}/invites/${encodeURIComponent(inviteId)}/send`, body);
  }

  /**
   * Login and get authentication token
   */
  async login(
    email: string,
    password: string
  ): Promise<{
    accessToken: string;
    refreshToken: string;
    user: any;
  }> {
    const response = await this.post<{
      accessToken: string;
      refreshToken: string;
      user: any;
    }>("/auth/login", { email, password }, { skipAuth: true });

    // Store tokens
    this.setAuthToken(response.accessToken, response.refreshToken);

    return response;
  }

  /**
   * Send employee invite for a location
   * POST /v1/users/my/locations/{locationId}/invites
   */
  async sendInvite(locationId: string, body: InviteUserRequest): Promise<InviteUserResponse> {
    return this.post<InviteUserResponse>(`/v1/users/my/locations/${locationId}/invites`, body);
  }

  /**
   * Delete a consumer user from an account
   * DELETE /v1/accounts/{consumerAccountId}/consumers/{consumerUserId}
   */
  async deleteConsumerUser(consumerAccountId: string, consumerUserId: string): Promise<void> {
    return this.delete(`/v1/accounts/${encodeURIComponent(consumerAccountId)}/consumers/${encodeURIComponent(consumerUserId)}`);
  }

  /**
   * Logout and clear tokens
   */
  async logout(): Promise<void> {
    try {
      await this.post("/auth/logout");
    } catch (error) {
      // Silently handle logout error
    } finally {
      this.clearAuthToken();
    }
  }
}

// ─── Shared types used by the Employees page ──────────────────────────────────

export type ResendInviteRequest = EmailTemplateInfo;

export interface ConsumerInvite {
  id: string;
  status: 'ACCEPTED' | 'PENDING' | 'REJECTED' | 'DELETED' | 'DECLINED' | string;
  sendToEmail: string;
  /** Name stored on the invite (populated for PENDING invites when submitted via the invite flow) */
  name?: string | null;
  /** First name from the invite payload (returned for PENDING invites by the v2 consumers endpoint) */
  firstName?: string | null;
  /** Last name from the invite payload (returned for PENDING invites by the v2 consumers endpoint) */
  lastName?: string | null;
  expiresAt?: string | null;
}

export interface ConsumerUserLocation {
  id: string;
  name: string;
}

export interface ConsumerUserAccount {
  id: string;
  locations: ConsumerUserLocation[];
}

// ─── Accounts-list shapes (returned by getConsumerAccounts v2) ─────────────────
// These contain full location+device detail, unlike ConsumerUserAccount which
// only carries {id, name} location stubs from the consumers list endpoint.

export interface ConsumerAccountWithLocations {
  id?: string;
  consumerAccountClassification?: string;
  locations?: ConsumerLocationDetail[];
}

export interface ConsumerUserWithLocations {
  id?: string;
  role?: string;
  consumerAccount?: ConsumerAccountWithLocations;
}

export interface AccountsData {
  id?: string;
  firstName?: string;
  lastName?: string;
  contactEmail?: string;
  countryCode?: string;
  locale?: string;
  consumerUsers?: ConsumerUserWithLocations[];
}

export interface AccountsResponse {
  data: AccountsData;
  errors?: string[];
}

// ─── Consumer user detail (v1 individual user endpoint) ───────────────────────

export interface ConsumerLocationAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvinceRegionCode?: string;
  zipPostalCode?: string;
  countryCode?: string;
}

export interface ConsumerDeviceProduct {
  productName?: string;
  productCategories?: string[];
  productFamily?: string;
  productPlatform?: string;
}

export interface ConsumerDeviceDetail {
  id?: string;
  deviceId?: string;
  product?: ConsumerDeviceProduct;
  globalDeviceType?: string;
}

export interface ConsumerDeviceItem {
  id: string;
  name: string | null;
  consumerDeviceLocationId?: string;
  device?: ConsumerDeviceDetail;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsumerLocationDetail {
  id: string;
  name: string;
  consumerAccountId?: string;
  address?: ConsumerLocationAddress;
  consumerDevices?: ConsumerDeviceItem[];
  weatherLocationId?: string;
}

export interface ConsumerUserDetailData {
  id: string;
  role: string;
  consumerAccount: {
    id: string;
    locations: ConsumerLocationDetail[];
  };
}

export interface ConsumerUserDetailResponse {
  data: ConsumerUserDetailData;
  errors: string[];
}

export interface ConsumerUser {
  id: string;
  user: {
    id: string | null;
    firstName: string | null;
    lastName: string | null;
    contactEmail: string | null;
  };
  invites: ConsumerInvite[];
  role: string;
  /** Present in v2 responses only */
  consumerAccount?: ConsumerUserAccount;
}

/**
 * Filter parameters for the v2 consumers endpoint.
 * Both fields are comma-separated strings.
 */
export interface ConsumerFilterBy {
  roles: string;
  locationIds: string;
}

export interface ConsumerV2PageInfo {
  startCursor: string;
  endCursor: string;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ConsumerV2UsersResponse {
  pageInfo: ConsumerV2PageInfo;
  data: ConsumerUser[];
  errors: string[];
}

// ─── Invite user types ────────────────────────────────────────────────────────

export interface ScheduleInfo {
  weekDays: string[];
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
}

export interface InviteDataInfo {
  firstName?: string;
  lastName?: string;
  role: string;
  sendToEmail: string;
  deviceIds?: string[];
  scheduleInfo?: ScheduleInfo;
}

export interface EmailTemplateInfo {
  templateLanguage: string;
  baseUrl: string;
  expiresAt: string;
}

export interface InviteUserRequest {
  InviteDataInfo: InviteDataInfo;
  emailTemplateInfo: EmailTemplateInfo;
}

export interface InviteUserResponse {
  data: {
    id: string;
    status: string;
    sentToEmail: string;
    link: string;
  };
  errors: string[];
}


// Singleton instance with environment-based base URL
const getBaseURL = (): string => {
  const env = import.meta.env.VITE_API_ENVIRONMENT as 'dev' | 'qa' | 'stage' | 'beta' | 'prod';

  const API_BASE_URLS = {
    dev: 'https://dev.api.honeywellhome.com/ris-public-api/api',
    qa: 'https://qa.api.honeywellhome.com/ris-public-api/api',
    stage: 'https://api.stg.resideo.com/ris-public-api/api',
    beta: 'https://b1prod-api.fortiq.titans.cloud/ris-public-api/api',
    prod: 'https://api.honeywellhome.com/ris-public-api/api',
  };

  return API_BASE_URLS[env] || API_BASE_URLS.qa;
};

export const apiService = new ApiService(getBaseURL());

/**
 * Per-device camera entitlement config (the shape returned inside each item's
 * `entitlementConfig.camera` in the devices/entitlementConfigs response).
 * Exported so callers (AuthContext cache, tests) can type per-device maps.
 */
export interface CameraEntitlementConfig {
  maxDeviceLimit?: number;
  liveStreamP2P?: number;
  liveStreamTurn?: number;
  /** Object detection event types enabled (e.g. ["person", "vehicle", "animal", "package"]) */
  objectDetectionEvents?: string[];
  /** Whether AI/semantic search over snapshots is available */
  aiSnapshotSearch?: boolean;
  /** Whether AI/semantic search over clips is available */
  aiClipSearch?: boolean;
  /** Whether snapshot capture is available */
  snapshot?: boolean;
  /** Whether on-demand clip recording is available */
  onDemandRecording?: boolean;
  storage?: {
    sdCardExport?: number;
    continousRecording?: boolean;
  };
  /** Audio event types enabled (e.g. ["carAlarm"]) */
  audio?: string[];
  /** Video-level features enabled (e.g. ["loitering"]) */
  video?: string[];
}

/**
 * Subscription entitlement configuration returned by the devices API.
 * Controls which features are available for a given subscription plan.
 */
export interface EntitlementConfig {
  camera?: CameraEntitlementConfig;
}

// Export for testing or custom instances
export default apiService;